import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ProfileUsers } from '../../../../../../api/enumerations';
import { getInAccreditation } from '../../../../../../api/theHive/redeProto';
import { InAccreditationItem } from '../../../../../../api/theHive/redeProto/types';
import { useCurrentRole } from '../../../../../../hooks/useCurrentRole';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

const PER_PAGE = 100;

export type InAccreditationOrder = 'fifo' | 'recent';

interface UseInAccreditationHook {
  role: number | null;
  isAllowed: boolean;
  items: InAccreditationItem[];
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  loading: boolean;
  expanded: number[];
  toggleExpand: (id: number) => void;
  order: InAccreditationOrder;
  setOrder: (order: InAccreditationOrder) => void;
  searchTerm: string;
  searchDisabled: boolean;
  handleSearchChange: (value: string) => void;
  handleClearSearch: () => void;
}

export default function useInAccreditation(): UseInAccreditationHook {
  const role = useCurrentRole();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const [items, setItems] = useState<InAccreditationItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [order, setOrderState] = useState<InAccreditationOrder>('fifo');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const debouncedSearch = useDebounce(searchTerm);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const isAllowed =
    role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

  const toggleExpand = useCallback((id: number): void => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  // Changing the order returns to page 1 (the top of the list changes).
  const setOrder = useCallback((next: InAccreditationOrder): void => {
    setOrderState(next);
    setPage(1);
  }, []);

  useEffect(() => {
    setAppliedSearch(debouncedSearch);
  }, [debouncedSearch]);

  const handleSearchChange = useCallback((value: string): void => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handleClearSearch = useCallback((): void => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(1);
  }, []);

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getInAccreditation(
        selectedNoticeId,
        page,
        PER_PAGE,
        order,
        appliedSearch
      );
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setItems(response.data ?? []);
      setTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedNoticeId, order, appliedSearch]);

  useEffect(() => {
    if (isAllowed) fetchData();
  }, [fetchData, isAllowed]);

  // Nothing to search with zero companies; stays enabled while a search is
  // active so the filter can be cleared.
  const searchDisabled = !appliedSearch && !loading && items.length === 0;

  return {
    role,
    isAllowed,
    items,
    page,
    setPage,
    totalPages,
    loading,
    expanded,
    toggleExpand,
    order,
    setOrder,
    searchTerm,
    searchDisabled,
    handleSearchChange,
    handleClearSearch,
  };
}
